<script generic="T" lang="ts" setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue"
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/vue/outline'

const props = withDefaults(defineProps<{
	items: T[]
	effect?: 'fade' | 'slide'
	interval?: number
	transitionDuration?: number
	pauseOnHover?: boolean
	swipeThreshold?: number
	ariaLabel?: string
}>(), {
	effect: 'slide',
	interval: 5000,
	transitionDuration: 800,
	pauseOnHover: true,
	swipeThreshold: 50,
	ariaLabel: 'Bildkarussell',
})

defineSlots<{
	default(props: { item: T; index: number; isActive: boolean }): unknown
}>()

const currentIndex = ref(0)
const progressKey = ref(0)
let timer: ReturnType<typeof setInterval> | null = null
const isPausedByHover = ref(false)

type SlideEffect = 'ken-burns' | 'parallax-ltr' | 'parallax-rtl'
const slideEffects = ref<SlideEffect[]>([])

const randomEffect = (): SlideEffect => {
	const r = Math.random()
	if (r < 0.5) return 'ken-burns'
	return r < 0.75 ? 'parallax-ltr' : 'parallax-rtl'
}

watch(() => props.items.length, (len) => {
	slideEffects.value = Array.from({length: len}, randomEffect)
}, {immediate: true})

const stopAutoplay = () => {
	if (timer) {
		clearInterval(timer)
		timer = null
	}
}

const startAutoplay = () => {
	stopAutoplay()
	if (props.items.length > 1 && !isPausedByHover.value) {
		timer = setInterval(navigateForward, props.interval)
	}
}

const navigateForward = () => {
	currentIndex.value = (currentIndex.value + 1) % props.items.length
	progressKey.value++
}

const navigateBack = () => {
	currentIndex.value = (currentIndex.value - 1 + props.items.length) % props.items.length
	progressKey.value++
}

const goTo = (index: number) => {
	currentIndex.value = index
	progressKey.value++
	if (!isPausedByHover.value) startAutoplay()
}

const clickPrev = () => goTo((currentIndex.value - 1 + props.items.length) % props.items.length)
const clickNext = () => goTo((currentIndex.value + 1) % props.items.length)

onMounted(startAutoplay)
onBeforeUnmount(stopAutoplay)

// ── Hover pause ──────────────────────────────────────────────────────────────

const onMouseEnter = () => {
	if (!props.pauseOnHover) return
	isPausedByHover.value = true
	stopAutoplay()
}

const onMouseLeave = () => {
	isPausedByHover.value = false
	if (props.pauseOnHover) {
		progressKey.value++
		startAutoplay()
	}
}

// ── Swipe / drag ─────────────────────────────────────────────────────────────

const dragStartX = ref<number | null>(null)
const didSwipe = ref(false)

const onPointerDown = (e: PointerEvent) => {
	dragStartX.value = e.clientX
	didSwipe.value = false
}

const onPointerUp = (e: PointerEvent) => {
	if (dragStartX.value === null) return
	const deltaX = e.clientX - dragStartX.value
	dragStartX.value = null

	if (Math.abs(deltaX) < props.swipeThreshold) return

	didSwipe.value = true
	deltaX < 0 ? navigateForward() : navigateBack()
	startAutoplay()
}

const onClickCapture = (e: MouseEvent) => {
	if (didSwipe.value) {
		e.preventDefault()
		e.stopPropagation()
		didSwipe.value = false
	}
}

// ── Styles ───────────────────────────────────────────────────────────────────

const slideTrackStyle = computed(() => ({
	transform: `translateX(-${currentIndex.value * 100}%)`,
	transition: `transform ${props.transitionDuration}ms ease-in-out`,
}))

const fadeSlideStyles = computed(() =>
		props.items.map((_item, index) => {
			const base = {transition: `opacity ${props.transitionDuration}ms ease-in-out`}
			const effect = slideEffects.value[index]
			if (index !== currentIndex.value || !effect) return base
			return {
				...base,
				animationName: effect,
				animationDuration: `${props.interval}ms`,
				animationTimingFunction: 'ease-in-out',
				animationFillMode: 'forwards',
			}
		})
)

const progressBarStyle = computed(() => ({
	animationDuration: `${props.interval}ms`,
	animationPlayState: isPausedByHover.value ? 'paused' : 'running',
}))
</script>

<template>
	<div
			:aria-label="ariaLabel"
			:class="{ 'bc--draggable': items.length > 1 }"
			class="bc"
			role="region"
			@mouseenter="onMouseEnter"
			@mouseleave="onMouseLeave"
			@pointerdown="onPointerDown"
			@pointerup="onPointerUp"
			@click.capture="onClickCapture"
			@dragstart.prevent
	>
		<!-- Slides viewport -->
		<div class="bc__viewport">
			<!-- Fade mode -->
			<template v-if="effect === 'fade'">
				<div aria-hidden="true" class="bc-fade__spacer">
					<slot :index="0" :is-active="false" :item="items[0]"/>
				</div>
				<div
						v-for="(item, index) in items"
						:key="index"
						:class="{ 'bc-fade__slide--active': index === currentIndex }"
						:style="fadeSlideStyles[index]"
						class="bc-fade__slide"
				>
					<slot :index="index" :is-active="index === currentIndex" :item="item"/>
				</div>
			</template>

			<!-- Slide mode -->
			<div v-else :style="slideTrackStyle" class="bc-slide__track">
				<div v-for="(item, index) in items" :key="index" class="bc-slide__item">
					<slot :index="index" :is-active="index === currentIndex" :item="item"/>
				</div>
			</div>

			<!-- Prev / Next arrows -->
			<template v-if="items.length > 1">
				<button aria-label="Vorheriges Bild"
				        class="bc__arrow bc__arrow--prev" @click="clickPrev" @pointerdown.stop
				        @pointerup.stop>
					<ChevronLeftIcon class="bc__arrow-icon"/>
				</button>
				<button aria-label="Nächstes Bild"
				        class="bc__arrow bc__arrow--next" @click="clickNext" @pointerdown.stop
				        @pointerup.stop>
					<ChevronRightIcon class="bc__arrow-icon"/>
				</button>
			</template>
		</div>

		<!-- Dot navigation + progress bar -->
		<template v-if="items.length > 1">
			<div :aria-label="ariaLabel" class="bc__dots" role="tablist">
				<button
						v-for="i in items.length"
						:key="i"
						:aria-label="`Bild ${i}`"
						:aria-selected="i - 1 === currentIndex"
						:class="{ 'bc__dot--active': i - 1 === currentIndex }"
						class="bc__dot"
						role="tab"
						@click="goTo(i - 1)" @pointerdown.stop @pointerup.stop
				/>
			</div>
			<div aria-hidden="true" class="bc__progress">
				<div :key="progressKey" :style="progressBarStyle" class="bc__progress-bar"/>
			</div>
		</template>

		<!-- Screen-reader live region -->
		<div aria-atomic="true" aria-live="polite" class="bc__sr-only">
			Bild {{ currentIndex + 1 }} von {{ items.length }}
		</div>
	</div>
</template>

<style scoped>
/* ── Outer wrapper ── */
.bc {
	width: 100%;
	user-select: none;
}

.bc--draggable {
	cursor: grab;
}

.bc--draggable:active {
	cursor: grabbing;
}

/* ── Viewport (clips slides and Ken Burns overflow) ── */
.bc__viewport {
	position: relative;
	width: 100%;
	overflow: hidden;
}

/* ── Fade slides ── */
.bc-fade__spacer {
	display: block;
	width: 100%;
	visibility: hidden;
	pointer-events: none;
}

.bc-fade__slide {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	opacity: 0;
	pointer-events: none;
}

@keyframes ken-burns {
	from {
		transform: scale(1);
	}
	to {
		transform: scale(1.04);
	}
}

@keyframes parallax-ltr {
	from {
		transform: scale(1.03) translateX(-1.5%);
	}
	to {
		transform: scale(1.03) translateX(1.5%);
	}
}

@keyframes parallax-rtl {
	from {
		transform: scale(1.03) translateX(1.5%);
	}
	to {
		transform: scale(1.03) translateX(-1.5%);
	}
}

.bc-fade__slide--active {
	opacity: 1;
	pointer-events: auto;
}


/* ── Slide track ── */
.bc-slide__track {
	display: flex;
	width: 100%;
	touch-action: pan-y;
}

.bc-slide__item {
	flex: 0 0 100%;
	width: 100%;
}

/* ── Arrows ── */
.bc__arrow {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	z-index: 10;
	background: rgba(0, 0, 0, 0.35);
	color: #fff;
	border: none;
	border-radius: 50%;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: background 0.2s;
	padding: 0;
}

.bc__arrow:hover {
	background: rgba(0, 0, 0, 0.6);
}

.bc__arrow--prev {
	left: 10px;
}

.bc__arrow--next {
	right: 10px;
}

.bc__arrow-icon {
	width: 18px;
	height: 18px;
}

/* ── Dots ── */
.bc__dots {
	display: flex;
	justify-content: center;
	gap: 6px;
	padding: 8px 0 4px;
}

.bc__dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #cbb19b; /* defaultborder */
	border: none;
	padding: 0;
	cursor: pointer;
	transition: background 0.2s, transform 0.2s;
}

.bc__dot--active {
	background: #182E41; /* primary */
	transform: scale(1.35);
}

/* ── Progress bar ── */
.bc__progress {
	height: 3px;
	background: #e8d1bd; /* brown-200 */
}

@keyframes bc-progress {
	from {
		transform: scaleX(0);
	}
	to {
		transform: scaleX(1);
	}
}

.bc__progress-bar {
	height: 100%;
	background: #182E41; /* primary */
	transform-origin: left;
	animation: bc-progress linear forwards;
}

/* ── Screen-reader only ── */
.bc__sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}
</style>

<!-- Keyframes must be global so inline animationName references resolve correctly -->
<style>
@keyframes ken-burns {
	from {
		transform: scale(1);
	}
	to {
		transform: scale(1.04);
	}
}

@keyframes parallax-ltr {
	from {
		transform: scale(1.03) translateX(-1.5%);
	}
	to {
		transform: scale(1.03) translateX(1.5%);
	}
}

@keyframes parallax-rtl {
	from {
		transform: scale(1.03) translateX(1.5%);
	}
	to {
		transform: scale(1.03) translateX(-1.5%);
	}
}
</style>

